import React, { useState, useMemo, useEffect } from 'react';

// Activity Timeline Component with grouping and filtering
const ActivityTimeline = ({ activities, loading }) => {
  const [filterType, setFilterType] = useState('all');
  const [displayLimit, setDisplayLimit] = useState(4);

  // Reset display limit when filter changes
  useEffect(() => {
    setDisplayLimit(4);
  }, [filterType]);

  const activityTypes = useMemo(() => {
    const types = new Set();
    if (activities && activities.length > 0) {
      activities.forEach(act => {
        if (act.type) types.add(act.type);
      });
    }
    return Array.from(types);
  }, [activities]);

  const filteredActivities = useMemo(() => {
    if (!activities || activities.length === 0) return [];
    let filtered = filterType === 'all' ? activities : activities.filter(act => act.type === filterType);
    return filtered;
  }, [activities, filterType]);

  const displayedActivities = useMemo(() => {
    return filteredActivities.slice(0, displayLimit);
  }, [filteredActivities, displayLimit]);

  const hasMore = filteredActivities.length > displayLimit;

  const groupedActivities = useMemo(() => {
    if (!displayedActivities || displayedActivities.length === 0) return {};
    
    const groups = {};
    displayedActivities.forEach(activity => {
      const date = new Date(activity.timestamp);
      const dateKey = date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
      
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(activity);
    });
    
    return groups;
  }, [displayedActivities]);

  const getActivityColor = (type) => {
    const colorMap = {
      'email_sent': '#568ded',
      'review_scheduled': '#4048EB',
      'pto_approved': '#40EB6E',
      'pto_rejected': '#EB4040',
      'compensation_updated': '#EBDD40',
      'document_uploaded': '#EB8D40',
      'note_added': '#CC40EB',
      'field_updated': '#568ded'
    };
    return colorMap[type] || '#568ded';
  };

  const getActivityIcon = (type) => {
    const iconMap = {
      'email_sent': 'fa-envelope',
      'review_scheduled': 'fa-calendar-check',
      'pto_approved': 'fa-check-circle',
      'pto_rejected': 'fa-times-circle',
      'compensation_updated': 'fa-dollar-sign',
      'document_uploaded': 'fa-file-upload',
      'note_added': 'fa-sticky-note',
      'field_updated': 'fa-edit'
    };
    return iconMap[type] || 'fa-circle';
  };

  const formatTimeAgo = (timestamp) => {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return new Date(timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div style={{ 
        color: '#FFFFFF', 
        opacity: 0.7,
        textAlign: 'center',
        padding: '40px 0',
        fontFamily: 'Montserrat',
        fontSize: '14px'
      }}>
        Loading activities...
      </div>
    );
  }

  if (!activities || activities.length === 0) {
    return (
      <div style={{ 
        color: '#FFFFFF', 
        opacity: 0.7,
        textAlign: 'center',
        padding: '40px 0',
        fontFamily: 'Montserrat',
        fontSize: '14px'
      }}>
        No activities yet.
      </div>
    );
  }

  return (
    <div>
      {/* Filter Bar */}
      {activityTypes.length > 1 && (
        <div style={{
          display: 'flex',
          gap: '8px',
          flexWrap: 'wrap',
          marginBottom: '24px',
          paddingBottom: '16px',
          borderBottom: '1px solid rgba(86, 141, 237, 0.2)'
        }}>
          <button
            onClick={() => setFilterType('all')}
            style={{
              padding: '6px 12px',
              backgroundColor: filterType === 'all' ? '#568ded' : 'rgba(86, 141, 237, 0.15)',
              border: `1px solid ${filterType === 'all' ? '#568ded' : 'rgba(86, 141, 237, 0.3)'}`,
              borderRadius: '20px',
              fontFamily: 'Montserrat',
              fontSize: '12px',
              fontWeight: 600,
              color: '#FFFFFF',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            All ({activities.length})
          </button>
          {activityTypes.map(type => (
            <button
              key={type}
              onClick={() => setFilterType(type)}
              style={{
                padding: '6px 12px',
                backgroundColor: filterType === type ? getActivityColor(type) : 'rgba(86, 141, 237, 0.15)',
                border: `1px solid ${filterType === type ? getActivityColor(type) : 'rgba(86, 141, 237, 0.3)'}`,
                borderRadius: '20px',
                fontFamily: 'Montserrat',
                fontSize: '12px',
                fontWeight: 600,
                color: '#FFFFFF',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                textTransform: 'capitalize'
              }}
            >
              {type.replace(/_/g, ' ')} ({activities.filter(a => a.type === type).length})
            </button>
          ))}
        </div>
      )}

      {/* Grouped Activities */}
      <div style={{ position: 'relative' }}>
        {Object.entries(groupedActivities).map(([dateKey, dateActivities], groupIdx) => (
          <div key={dateKey} style={{ marginBottom: groupIdx < Object.keys(groupedActivities).length - 1 ? '32px' : '0' }}>
            {/* Date Header - positioned above first node */}
            <div style={{
              marginBottom: '16px',
              fontFamily: 'Montserrat',
              fontSize: '12px',
              fontWeight: 700,
              color: 'rgba(255, 255, 255, 0.6)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              whiteSpace: 'nowrap'
            }}>
              {dateKey}
            </div>

            {/* Activities for this date */}
            <div style={{ position: 'relative', paddingLeft: '32px' }}>
              {dateActivities.map((activity, idx) => {
                const activityColor = getActivityColor(activity.type);
                return (
                  <div key={activity.id || idx} style={{ 
                    position: 'relative', 
                    marginBottom: idx < dateActivities.length - 1 ? '20px' : '0',
                    paddingLeft: '32px'
                  }}>
                    {/* Icon */}
                    <div style={{
                      position: 'absolute',
                      left: 0,
                      top: 4,
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      backgroundColor: `${activityColor}20`,
                      border: `2px solid ${activityColor}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: 0,
                      margin: 0
                    }}>
                      <i className={`fa-solid ${getActivityIcon(activity.type)}`} style={{ 
                        fontSize: '10px', 
                        color: activityColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        height: '100%',
                        lineHeight: '24px'
                      }}></i>
                    </div>

                    {/* Connector Line */}
                    {idx < dateActivities.length - 1 && (
                      <div style={{
                        position: 'absolute',
                        left: '11px',
                        top: '28px',
                        width: '2px',
                        height: 'calc(100% + 4px)',
                        backgroundColor: 'rgba(86, 141, 237, 0.2)'
                      }} />
                    )}

                    {/* Activity Content */}
                    <div style={{
                      backgroundColor: 'rgba(86, 141, 237, 0.08)',
                      border: `1px solid ${activityColor}30`,
                      borderRadius: '12px',
                      padding: '16px',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(86, 141, 237, 0.12)';
                      e.currentTarget.style.borderColor = `${activityColor}50`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(86, 141, 237, 0.08)';
                      e.currentTarget.style.borderColor = `${activityColor}30`;
                    }}
                    >
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '8px'
                      }}>
                        <span style={{
                          fontFamily: 'Montserrat',
                          fontSize: '13px',
                          fontWeight: 700,
                          color: activityColor,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          {activity.type?.replace(/_/g, ' ')}
                        </span>
                        <span style={{
                          fontFamily: 'Montserrat',
                          fontSize: '11px',
                          color: 'rgba(255, 255, 255, 0.5)',
                          marginLeft: 'auto'
                        }}>
                          {formatTimeAgo(activity.timestamp)}
                        </span>
                      </div>
                      
                      <div style={{
                        fontFamily: 'Montserrat',
                        fontSize: '14px',
                        color: 'rgba(255, 255, 255, 0.9)',
                        marginBottom: '8px',
                        lineHeight: 1.5
                      }}>
                        {activity.description}
                      </div>

                      {/* Before/After Values */}
                      {(activity.before || activity.after) && (
                        <div style={{
                          display: 'flex',
                          gap: '12px',
                          marginTop: '8px',
                          paddingTop: '8px',
                          borderTop: '1px solid rgba(86, 141, 237, 0.15)'
                        }}>
                          {activity.before && (
                            <div style={{ flex: 1 }}>
                              <div style={{
                                fontFamily: 'Montserrat',
                                fontSize: '11px',
                                color: 'rgba(255, 255, 255, 0.6)',
                                marginBottom: '4px'
                              }}>
                                Before
                              </div>
                              <div style={{
                                fontFamily: 'monospace',
                                fontSize: '12px',
                                color: '#EB4040'
                              }}>
                                {typeof activity.before === 'object' ? JSON.stringify(activity.before, null, 2) : String(activity.before)}
                              </div>
                            </div>
                          )}
                          {activity.after && (
                            <div style={{ flex: 1 }}>
                              <div style={{
                                fontFamily: 'Montserrat',
                                fontSize: '11px',
                                color: 'rgba(255, 255, 255, 0.6)',
                                marginBottom: '4px'
                              }}>
                                After
                              </div>
                              <div style={{
                                fontFamily: 'monospace',
                                fontSize: '12px',
                                color: '#40EB6E'
                              }}>
                                {typeof activity.after === 'object' ? JSON.stringify(activity.after, null, 2) : String(activity.after)}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {activity.by && (
                        <div style={{
                          fontFamily: 'Montserrat',
                          fontSize: '12px',
                          color: 'rgba(255, 255, 255, 0.6)',
                          marginTop: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          <i className="fa-solid fa-user" style={{ fontSize: '10px' }}></i>
                          {activity.by}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        
        {/* Load More Button */}
        {hasMore && (
          <div style={{ marginTop: '24px', textAlign: 'center' }}>
            <button
              onClick={() => setDisplayLimit(prev => prev + 10)}
              style={{
                background: 'none',
                border: 'none',
                color: '#568ded',
                fontFamily: 'Montserrat',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                textDecoration: 'underline',
                padding: 0
              }}
            >
              Load More
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityTimeline;

